import React from 'react';
import { Form } from 'antd';
import { MinusCircleOutlined } from '@ant-design/icons';
import { FormItem } from '@xbcb/form-item-components';
import { NamePath } from '@xbcb/ui-types';
import { useTheme } from 'styled-components';
import { LotNumberAssigner } from '__generated__/graphql';
import FormListSection from '../FormListSection';
import {
  StyledSection,
  StyledSectionHeader,
  StyledFieldRow,
  StyledSelect,
  StyledInput,
  StyledDatePicker,
  StyledRemoveButton,
} from './styles';

const LOT_NUMBER_ASSIGNER_OPTIONS = [
  { value: LotNumberAssigner.MANUFACTURER, label: 'Manufacturer' },
  { value: LotNumberAssigner.SELLER, label: 'Seller' },
];

export interface LotDetailsProps {
  fullNamePath?: NamePath;
  localNamePath: NamePath;
  readOnly?: boolean;
  disabled?: boolean;
}

const LotDetails: React.FC<LotDetailsProps> = ({
  fullNamePath,
  localNamePath,
  readOnly = false,
  disabled = false,
}) => {
  const theme = useTheme();
  const form = Form.useFormInstance();
  const watchPath = fullNamePath || localNamePath;
  const lotInformation =
    Form.useWatch([...(watchPath as string[]), 'lotInformation'], form) || [];

  return (
    <StyledSection>
      <StyledSectionHeader>Lot Details</StyledSectionHeader>

      <FormListSection
        name={[...localNamePath, 'lotInformation']}
        addButtonLabel="Add Lot Details"
        readOnly={readOnly}
        disabled={disabled}
        renderRow={(field, index, { remove, showRemove }) => (
          <StyledFieldRow key={field.key}>
            <FormItem
              name={[field.name, 'lotNumberAssigner']}
              label="Lot Number Assigned By (optional)"
              $itemSize={theme.size.short}
              $readOnly={readOnly}
            >
              <StyledSelect
                options={LOT_NUMBER_ASSIGNER_OPTIONS}
                disabled={disabled || readOnly}
                placeholder="Select assigner"
                allowClear
              />
            </FormItem>
            <FormItem
              name={[field.name, 'lotNumber']}
              label="Lot Number"
              $itemSize={theme.size.tiny_short}
              $readOnly={readOnly}
              rules={[
                {
                  required: !!lotInformation[index]?.lotNumberAssigner,
                },
              ]}
              validateTrigger="onBlur"
            >
              <StyledInput
                maxLength={25}
                disabled={disabled || readOnly}
                placeholder="Enter lot number"
              />
            </FormItem>
            <FormItem
              name={[field.name, 'productionStartDate']}
              label="Production Start"
              $itemSize={theme.size.tiny_short}
              $readOnly={readOnly}
            >
              <StyledDatePicker
                format="MM/DD/YYYY"
                disabled={disabled || readOnly}
                placeholder="MM/DD/YYYY"
              />
            </FormItem>
            <FormItem
              name={[field.name, 'productionEndDate']}
              label="Production End"
              $itemSize={theme.size.tiny_short}
              $readOnly={readOnly}
            >
              <StyledDatePicker
                format="MM/DD/YYYY"
                disabled={disabled || readOnly}
                placeholder="MM/DD/YYYY"
              />
            </FormItem>
            {showRemove && (
              <StyledRemoveButton
                type="text"
                danger
                icon={<MinusCircleOutlined />}
                onClick={remove}
                disabled={disabled}
              >
                Remove
              </StyledRemoveButton>
            )}
          </StyledFieldRow>
        )}
      />
    </StyledSection>
  );
};

export default LotDetails;
